import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { formatMoney, currencySymbol } from "../lib/money";
import { Download, ArrowLeft } from "lucide-react";

/**
 * Sprint 4 — Invoice viewer.
 *
 * Renders an India-format or international-format invoice from the
 * billing data returned by /api/billing/invoices/{id}. Designed to print
 * cleanly via window.print().
 */
export default function InvoiceViewerPage() {
  const { invoiceId } = useParams();
  const [inv, setInv] = useState(null);

  useEffect(() => {
    api.get(`/billing/invoices/${invoiceId}`).then((r) => setInv(r.data));
  }, [invoiceId]);

  if (!inv) {
    return <DashboardLayout>
      <p className="text-sm text-slate-500">Loading invoice…</p>
    </DashboardLayout>;
  }

  const isInr = inv.billing_entity === "factskoot";
  const customer = inv.indian_billing || inv.intl_billing || {};
  const tax = inv.tax || {};

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="invoice-viewer-root">
        <div className="flex items-center justify-between print:hidden mb-4">
          <Link to="/dashboard/billing" className="text-sm text-[#0F4C81] flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to billing
          </Link>
          <button
            type="button"
            onClick={() => window.print()}
            className="px-3 py-1.5 rounded-md bg-[#0F4C81] text-white text-xs font-semibold flex items-center gap-1.5"
            data-testid="invoice-download"
          >
            <Download className="h-3.5 w-3.5" /> Print / Save PDF
          </button>
        </div>

        <article className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm" data-testid="invoice-body">
          <header className="flex items-start justify-between border-b border-slate-200 pb-5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.36em] text-[#D4AF37] font-bold">
                {isInr ? "Tax Invoice (India)" : "Invoice (International)"}
              </p>
              <h1 className="mt-1 text-xl font-bold text-[#0A192F]" data-testid="invoice-supplier-name">{inv.supplier.legal_name}</h1>
              <p className="mt-1 text-xs text-slate-600">
                {inv.supplier.address_line_1}{inv.supplier.address_line_2 ? ", " + inv.supplier.address_line_2 : ""}<br />
                {inv.supplier.city}, {isInr ? `${inv.supplier.state} ${inv.supplier.postal_code}` : "UAE"}, {inv.supplier.country}<br />
                {isInr ? (
                  <>Email: {inv.supplier.email} · WhatsApp: {inv.supplier.whatsapp} · Phone: {inv.supplier.phone}<br /></>
                ) : (
                  <>Email: {inv.supplier.email} · Mobile: {inv.supplier.mobile} · WhatsApp: {inv.supplier.whatsapp}<br /></>
                )}
                Website: {inv.supplier.website}<br />
                {isInr ? <>State Code: <strong>{inv.supplier.state_code}</strong> · GSTIN: <em>{inv.supplier.gstin_placeholder}</em></> :
                         <>VAT TRN: <em>{inv.supplier.vat_trn_placeholder}</em></>}
              </p>
            </div>
            <div className="text-right text-xs">
              <p className="font-mono text-[#0A192F]" data-testid="invoice-number">{inv.invoice_number}</p>
              <p className="text-slate-500 mt-1">{inv.issued_at?.slice(0, 10)}</p>
              {inv.receipt_number && (
                <p className="text-slate-500 mt-1">Receipt: <span className="font-mono">{inv.receipt_number}</span></p>
              )}
            </div>
          </header>

          <section className="grid grid-cols-2 gap-6 mt-5">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Bill to</p>
              <p className="mt-1 text-sm font-semibold text-[#0A192F]">
                {customer.billing_name || "—"}
              </p>
              {customer.organisation_name && <p className="text-xs">{customer.organisation_name}</p>}
              <p className="mt-1 text-xs text-slate-600">
                {customer.address_line_1}<br />
                {customer.address_line_2 && <>{customer.address_line_2}<br /></>}
                {customer.city}, {customer.state || customer.state_code} {customer.postal_code}<br />
                {customer.country}
              </p>
              {customer.gstin && <p className="mt-1 text-xs">GSTIN: <strong>{customer.gstin}</strong></p>}
              {customer.state_code && <p className="text-xs">Customer state code: <strong>{customer.state_code}</strong></p>}
              {customer.tax_id && <p className="mt-1 text-xs">Tax ID: <strong>{customer.tax_id}</strong></p>}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Payment</p>
              <p className="text-xs text-slate-600 mt-1">Plan: <strong>{inv.plan_name || inv.plan_id}</strong></p>
              <p className="text-xs text-slate-600">Currency: <strong>{inv.currency}</strong></p>
              {isInr && <p className="text-xs text-slate-600">Service category: Other Educational Services</p>}
            </div>
          </section>

          <section className="mt-6 border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left p-3">Description</th>
                  <th className="text-right p-3">Amount ({currencySymbol(inv.currency)} · {inv.currency})</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-slate-100">
                  <td className="p-3">{inv.plan_name || inv.plan_id}</td>
                  <td className="p-3 text-right font-medium">{formatMoney(inv.amount, inv.currency)}</td>
                </tr>
                {isInr && tax.gst_type === "CGST_SGST" && (
                  <>
                    <tr className="border-t border-slate-100"><td className="p-3">CGST 9%</td><td className="p-3 text-right">{formatMoney(tax.cgst, inv.currency)}</td></tr>
                    <tr className="border-t border-slate-100"><td className="p-3">SGST 9%</td><td className="p-3 text-right">{formatMoney(tax.sgst, inv.currency)}</td></tr>
                  </>
                )}
                {isInr && tax.gst_type === "IGST" && (
                  <tr className="border-t border-slate-100"><td className="p-3">IGST 18%</td><td className="p-3 text-right">{formatMoney(tax.igst, inv.currency)}</td></tr>
                )}
                {!isInr && (
                  <tr className="border-t border-slate-100"><td className="p-3">UAE VAT</td><td className="p-3 text-right">Not charged</td></tr>
                )}
                <tr className="border-t-2 border-slate-300 font-bold">
                  <td className="p-3">Total payable</td>
                  <td className="p-3 text-right">{formatMoney(inv.total_payable, inv.currency)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          {!isInr && (
            <p className="mt-4 text-[11px] text-slate-500 italic">
              UAE VAT is not charged currently as per current tax settings. VAT may apply in
              future if enabled according to applicable UAE law.
            </p>
          )}

          <footer className="mt-8 pt-5 border-t border-slate-200 text-[11px] text-slate-500 leading-relaxed" data-testid="invoice-footer">
            <p>Refunds are governed by the Diplomexa Refund &amp; Cancellation Policy and follow the original billing entity and payment gateway.</p>
            <p>Payment-gateway charges, taxes and administrative fees may be non-refundable where applicable.</p>
            <p className="mt-2">
              For billing queries on this invoice, please contact{" "}
              {isInr ? (
                <>
                  <a className="text-[#0F4C81]" href="mailto:info@factskoot.com">info@factskoot.com</a>{" "}
                  · +91-80-62177852 · WhatsApp +91 9844139333
                </>
              ) : (
                <>
                  <a className="text-[#0F4C81]" href="mailto:info@destineze.com">info@destineze.com</a>{" "}
                  · Mobile / WhatsApp +971 50 488 3656
                </>
              )}.
            </p>
            <p className="mt-3 text-[#D4AF37] font-semibold uppercase tracking-wider text-[10px]">Powered by Diplomexa</p>
          </footer>
        </article>
      </div>
    </DashboardLayout>
  );
}
